import React, { useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { WeddingBooking, WeddingVendor } from '../../../types';
import { format } from 'date-fns';
import { Calendar, Clock, CheckCircle, XCircle, MoreVertical, Search, Filter, Package, MessageSquare, MessageCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { useChat } from '../../../context/ChatContext';
import { useNavigate } from 'react-router-dom';

interface ExtendedBooking extends WeddingBooking {
    package?: {
        name: string;
        price: number;
    };
    user_email?: string;
}

export default function VendorBookings() {
    const [bookings, setBookings] = useState<ExtendedBooking[]>([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState<'all' | 'pending' | 'confirmed' | 'cancelled' | 'rejected'>('all');
    const [processingId, setProcessingId] = useState<string | null>(null);
    const { startChatWithContext } = useChat();
    const navigate = useNavigate();

    useEffect(() => {
        fetchVendorBookings();
    }, []);

    const fetchVendorBookings = async () => {
        try {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) return;

            // 1. Get vendor ID owned by this user
            const { data: vendor } = await supabase
                .from('wedding_vendors')
                .select('id')
                .eq('owner_id', session.user.id)
                .single();

            if (!vendor) return;

            // 2. Fetch bookings with package details
            const { data: bookingsData, error } = await supabase
                .from('wedding_bookings')
                .select(`
                    *,
                    package:wedding_packages (
                        name,
                        price
                    )
                `)
                .eq('vendor_id', vendor.id)
                .order('created_at', { ascending: false });

            if (error) throw error;

            // 3. Fetch user profiles manually (since direct join might be tricky without explicit FK to public.profiles)
            const userIds = [...new Set(bookingsData.map((b: any) => b.user_id))];
            const { data: profiles } = await supabase
                .from('profiles')
                .select('id, email')
                .in('id', userIds);

            const profileMap = new Map(profiles?.map(p => [p.id, p.email]) || []);

            const enrichedBookings = bookingsData.map((b: any) => ({
                ...b,
                user_email: profileMap.get(b.user_id) || 'Unknown User'
            }));

            setBookings(enrichedBookings);
        } catch (error) {
            console.error('Error fetching bookings:', error);
            toast.error('Failed to load bookings');
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateStatus = async (bookingId: string, newStatus: 'confirmed' | 'rejected') => {
        if (!confirm(`Are you sure you want to ${newStatus === 'confirmed' ? 'Accept' : 'Decline'} this booking?`)) return;

        setProcessingId(bookingId);
        try {
            const { error } = await supabase
                .from('wedding_bookings')
                .update({ status: newStatus })
                .eq('id', bookingId);

            if (error) throw error;

            toast.success(`Booking ${newStatus === 'confirmed' ? 'Accepted' : 'Declined'}`);
            setBookings(prev => prev.map(b => b.id === bookingId ? { ...b, status: newStatus } : b));
        } catch (error) {
            console.error('Error updating booking:', error);
            toast.error(`Failed to ${newStatus === 'confirmed' ? 'accept' : 'decline'} booking`);
        } finally {
            setProcessingId(null);
        }
    };

    const handleChat = async (booking: ExtendedBooking) => {
        if (!booking.user_id) return;

        try {
            const dateStr = format(new Date(booking.booking_date), 'PPP');
            const contextMessage = `Hi, I'm reaching out regarding your booking request for ${dateStr}.`;

            await startChatWithContext(booking.user_id, contextMessage);
            navigate('/weddings/messages');
        } catch (error) {
            console.error('Failed to start chat:', error);
            toast.error('Failed to start chat');
        }
    };

    const filteredBookings = bookings.filter(b => {
        if (filter === 'all') return true;
        return b.status === filter;
    });

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-rose-600"></div>
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Booking Requests</h1>

            {/* Filters */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 mb-6 flex flex-wrap gap-2">
                {(['all', 'pending', 'confirmed', 'cancelled', 'rejected'] as const).map((f) => (
                    <button
                        key={f}
                        onClick={() => setFilter(f)}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-colors capitalize ${filter === f
                            ? 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300'
                            : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                            }`}
                    >
                        {f}
                    </button>
                ))}
            </div>

            {/* List */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
                {filteredBookings.length === 0 ? (
                    <div className="text-center py-12">
                        <Calendar className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white">No bookings found</h3>
                        <p className="text-gray-500 dark:text-gray-400 mt-2">
                            {filter === 'all'
                                ? "You haven't received any booking requests yet."
                                : `No ${filter} bookings found.`}
                        </p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gray-50 dark:bg-gray-900/50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date & Time</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                {filteredBookings.map((booking) => (
                                    <tr key={booking.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                        <td className="px-6 py-4 whitespace-nowrap align-top">
                                            <div className="flex flex-col">
                                                <span className="text-sm font-medium text-gray-900 dark:text-white">
                                                    {format(new Date(booking.booking_date), 'PPP')}
                                                </span>
                                                <div className="flex items-center gap-1.5 mt-1 text-xs text-gray-500 capitalize">
                                                    <Clock size={12} />
                                                    {booking.booking_type.replace('_', ' ')}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap align-top">
                                            <div className="flex flex-col">
                                                <div className="text-sm font-medium text-gray-900 dark:text-white">
                                                    {booking.user_email || 'Unknown User'}
                                                </div>
                                                <div className="text-xs text-gray-400 mt-0.5">
                                                    ID: {booking.user_id.slice(0, 8)}...
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 align-top">
                                            <div className="flex flex-col gap-2">
                                                {/* Price */}
                                                <div className="text-sm font-medium text-gray-900 dark:text-white">
                                                    ₹{booking.total_price.toLocaleString()}
                                                </div>

                                                {/* Package Info */}
                                                {booking.package && (
                                                    <div className="flex items-center gap-1.5 text-xs text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20 px-2 py-1 rounded w-fit">
                                                        <Package size={12} />
                                                        <span className="font-medium">{booking.package.name}</span>
                                                    </div>
                                                )}

                                                {/* Guest Count Info */}
                                                {booking.guest_count && booking.guest_count > 0 && (
                                                    <div className="flex items-center gap-1.5 text-xs text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 px-2 py-1 rounded w-fit">
                                                        <Users size={12} />
                                                        <span className="font-medium">{booking.guest_count} Guests</span>
                                                    </div>
                                                )}

                                                {/* User Note */}
                                                {booking.user_note && (
                                                    <div className="flex gap-1.5 items-start mt-1 max-w-xs">
                                                        <MessageSquare size={12} className="text-gray-400 mt-0.5 shrink-0" />
                                                        <p className="text-xs text-gray-600 dark:text-gray-400 italic">
                                                            "{booking.user_note}"
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap align-top">
                                            <span className={`px-2 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full capitalize
                                                ${booking.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                                                    booking.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                                        booking.status === 'cancelled' || booking.status === 'rejected' ? 'bg-red-100 text-red-800' :
                                                            'bg-gray-100 text-gray-800'}`}>
                                                {booking.status}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium align-top">
                                            <div className="flex justify-end gap-2 items-center">
                                                <button
                                                    onClick={() => handleChat(booking)}
                                                    className="text-gray-400 hover:text-rose-600 p-1 rounded transition-colors"
                                                    title="Chat with Customer"
                                                >
                                                    <MessageCircle size={20} />
                                                </button>

                                                {booking.status === 'pending' && (
                                                    <>
                                                        <button
                                                            onClick={() => handleUpdateStatus(booking.id, 'confirmed')}
                                                            disabled={!!processingId}
                                                            className="text-green-600 hover:text-green-900 disabled:opacity-50 p-1 hover:bg-green-50 rounded"
                                                            title="Accept"
                                                        >
                                                            <CheckCircle size={20} />
                                                        </button>
                                                        <button
                                                            onClick={() => handleUpdateStatus(booking.id, 'rejected')}
                                                            disabled={!!processingId}
                                                            className="text-red-600 hover:text-red-900 disabled:opacity-50 p-1 hover:bg-red-50 rounded"
                                                            title="Reject"
                                                        >
                                                            <XCircle size={20} />
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
